import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useBranchContext } from "@/contexts/BranchContext";
import { useFeatureEntitlements } from "@/hooks/useFeatureEntitlements";
import { usePaywallModal } from "@/contexts/PaywallModalContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { subDays } from "date-fns";
import { Users, ArrowLeft } from "lucide-react";

const RANGE_DAYS = 30;
const INACTIVE_QUIET_DAYS = 14;

function saleAmount(final_amount: number | null, total_price: number | null, q: number | null, unit: number | null): number {
  const amt = final_amount ?? total_price ?? (q && unit ? q * unit : 0);
  return Number(amt || 0);
}

function formatK(n: number): string {
  return `K${Math.round(n).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
}

type ProfileRow = { user_id: string; full_name: string | null; email: string | null };

export default function StaffPerformancePage() {
  const { businessId } = useAuth();
  const { selectedBranchId } = useBranchContext();
  const { staffPerformance } = useFeatureEntitlements();
  const { openPaywall } = usePaywallModal();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [profiles, setProfiles] = useState<ProfileRow[]>([]);
  const [rows, setRows] = useState<
    {
      user_id: string;
      label: string;
      sales_total: number;
      sales_count: number;
      expenses_total: number;
      expenses_count: number;
      last_sale_at: string | null;
      last_expense_at: string | null;
      status: "active" | "quiet";
    }[]
  >([]);

  useEffect(() => {
    if (staffPerformance === "none") {
      openPaywall("staff_performance");
    }
  }, [staffPerformance, openPaywall]);

  const fetchData = useCallback(async () => {
    if (!businessId || staffPerformance === "none") {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const since = subDays(new Date(), RANGE_DAYS).toISOString();
    const branchFilter = selectedBranchId !== "all" ? selectedBranchId : null;

    try {
      const profRes = await supabase
        .from("profiles")
        .select("user_id, full_name, email")
        .eq("business_id", businessId)
        .order("full_name", { ascending: true });
      if (profRes.error) throw profRes.error;
      const profs = (profRes.data ?? []) as ProfileRow[];
      setProfiles(profs);

      let salesQ = supabase
        .from("sales")
        .select("sold_by, quantity, final_amount, total_price, unit_price, created_at, voided_at, branch_id")
        .eq("business_id", businessId)
        .gte("created_at", since)
        .is("voided_at", null);
      if (branchFilter) salesQ = salesQ.eq("branch_id", branchFilter);

      let expQ = supabase
        .from("expenses")
        .select("created_by, amount, expense_date, date, branch_id")
        .eq("business_id", businessId)
        .gte("expense_date", since.slice(0, 10));
      if (branchFilter) expQ = expQ.eq("branch_id", branchFilter);

      const [salesRes, expRes] = await Promise.all([salesQ, expQ]);
      if (salesRes.error) throw salesRes.error;
      if (expRes.error) throw expRes.error;

      const salesAgg = new Map<
        string,
        { total: number; count: number; lastAt: string | null }
      >();
      for (const s of salesRes.data ?? []) {
        const uid = (s as { sold_by: string }).sold_by;
        if (!uid) continue;
        const amt = saleAmount(
          (s as { final_amount: number | null }).final_amount,
          (s as { total_price: number | null }).total_price,
          (s as { quantity: number | null }).quantity,
          (s as { unit_price: number | null }).unit_price
        );
        const cur = salesAgg.get(uid) ?? { total: 0, count: 0, lastAt: null };
        cur.total += amt;
        cur.count += 1;
        const ca = (s as { created_at: string }).created_at;
        if (!cur.lastAt || ca > cur.lastAt) cur.lastAt = ca;
        salesAgg.set(uid, cur);
      }

      const expAgg = new Map<string, { total: number; count: number; lastAt: string | null }>();
      for (const e of expRes.data ?? []) {
        const uid = (e as { created_by: string }).created_by;
        const cur = expAgg.get(uid) ?? { total: 0, count: 0, lastAt: null };
        cur.total += Number((e as { amount: number }).amount || 0);
        cur.count += 1;
        const d = (e as { date?: string; expense_date: string }).date ?? (e as { expense_date: string }).expense_date;
        if (d && (!cur.lastAt || d > cur.lastAt)) cur.lastAt = d;
        expAgg.set(uid, cur);
      }

      const now = Date.now();
      const quietMs = INACTIVE_QUIET_DAYS * 24 * 60 * 60 * 1000;

      const merged = profs.map((p) => {
        const sa = salesAgg.get(p.user_id) ?? { total: 0, count: 0, lastAt: null };
        const ea = expAgg.get(p.user_id) ?? { total: 0, count: 0, lastAt: null };
        const lastSaleMs = sa.lastAt ? new Date(sa.lastAt).getTime() : 0;
        const lastExpMs = ea.lastAt ? new Date(ea.lastAt).getTime() : 0;
        const lastActivity = Math.max(lastSaleMs, lastExpMs);
        const quiet = lastActivity === 0 || now - lastActivity > quietMs;
        const label = (p.full_name?.trim() || p.email || p.user_id.slice(0, 8)).trim();
        return {
          user_id: p.user_id,
          label,
          sales_total: sa.total,
          sales_count: sa.count,
          expenses_total: ea.total,
          expenses_count: ea.count,
          last_sale_at: sa.lastAt,
          last_expense_at: ea.lastAt,
          status: quiet ? ("quiet" as const) : ("active" as const),
        };
      });

      merged.sort((a, b) => b.sales_total - a.sales_total);
      setRows(merged);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load staff metrics");
    } finally {
      setLoading(false);
    }
  }, [businessId, staffPerformance, selectedBranchId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (staffPerformance === "none") {
    return (
      <div className="space-y-4 p-4 max-w-3xl mx-auto">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/" className="gap-2">
            <ArrowLeft className="h-4 w-4" /> Back
          </Link>
        </Button>
        <Card>
          <CardHeader>
            <CardTitle>Staff performance</CardTitle>
            <CardDescription>Pro Solo and Pro Growth include team visibility; upgrade to see seller rankings and activity.</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 max-w-5xl mx-auto">
      <div>
        <Button variant="ghost" size="sm" asChild className="mb-2 -ml-2">
          <Link to="/" className="gap-2 text-muted-foreground">
            <ArrowLeft className="h-4 w-4" /> Dashboard
          </Link>
        </Button>
        <h1 className="text-2xl font-display font-bold flex items-center gap-2">
          <Users className="h-7 w-7 text-primary" />
          Staff performance
        </h1>
        <p className="text-sm text-muted-foreground">
          Last {RANGE_DAYS} days — same branch filter as the header. “Quiet” means no sales or expenses recorded in {INACTIVE_QUIET_DAYS}+ days (not a judgment on shifts).
        </p>
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</div>
      )}

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Team ({profiles.length})</CardTitle>
            <CardDescription>Aggregates from recorded sales and expenses only.</CardDescription>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            {rows.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4">No team profiles for this business yet.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left">
                    <th className="py-2 pr-2">Team member</th>
                    <th className="py-2 pr-2 text-right">Sales ({RANGE_DAYS}d)</th>
                    <th className="py-2 pr-2 text-right">#Sales</th>
                    <th className="py-2 pr-2 text-right">Expenses logged</th>
                    <th className="py-2 pr-2 text-right">#Exp.</th>
                    <th className="py-2 text-right">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.user_id} className="border-b border-border/60">
                      <td className="py-2 pr-2 font-medium">{r.label}</td>
                      <td className="py-2 pr-2 text-right">{formatK(r.sales_total)}</td>
                      <td className="py-2 pr-2 text-right text-muted-foreground">{r.sales_count}</td>
                      <td className="py-2 pr-2 text-right">{formatK(r.expenses_total)}</td>
                      <td className="py-2 pr-2 text-right text-muted-foreground">{r.expenses_count}</td>
                      <td className="py-2 text-right">
                        <Badge variant={r.status === "active" ? "default" : "secondary"}>
                          {r.status === "active" ? "Active" : "Quiet"}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
